import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { internalBaseEquipments } from '@/common/constants/internalBaseEquipments'
import { useBillingEvents } from '@/common/hooks/queries/useBillingEvents'
import { useCardWithSecureInformation } from '@/common/hooks/queries/useCardWithSecureInformation'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { BillingEventData } from '@/common/types/billing/event.type'
import { CompanyBillingEventTransformer } from '@/features/privateCompanies/services/companyBillingEventTransformer'
import { CompanyBillingEventTableItem } from '@/features/privateCompanies/types/companyEvents.type'
import { SearchIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { createSearchParams, useNavigate, useParams } from 'react-router-dom'
import { defaultColumns } from './columns'
import Filters from './components/Filters'
import LoadingSpinner from '@/common/components/LoadingSpinner'
import { formatCurrency } from '@/common/utils/formatters'

interface EventsListProps {
  remainingCredit?: string
  remainingBalance?: string
  companyKind?: string
}

const List = ({
  remainingBalance,
  remainingCredit,
  companyKind
 }: EventsListProps) => {
  const { id: companyId } = useParams()
  const navigate = useNavigate()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { filterParams, setFilterParams } = useFilterParams({
    fromInternalBase: internalBaseEquipments.find(({ value }) => value === 'false'),
  })

  const { formattedDate } = useFilterDate(filterParams)

  const [search, setSearch, instantSearch, setInstantSearch] = useDebounce(filterParams.search || '')

  const { mutate: cardData, isPending: cardDataIsPending } = useCardWithSecureInformation({})

  const handleRedirectToConsumption = ({
    cardId,
    establishment,
    product,
  }: {
    cardId: string
    establishment: { id: string; label: string }
    product: { id: string; displayName: string }
  }) => {
    cardData(cardId, {
      onSuccess: (response) => {
        const cardNumber = response?.data?.data?.number

        if (cardNumber) {
          navigate(
            `/card-current-limit?cardIdentifier=${cardNumber}&establishmentId=${establishment.id}&establishmentLabel=${establishment.label}&productId=${product.id}&productDisplayName=${product.displayName}`,
          )
        }
      },
      onError: (error) => {
        setMessages(error.response?.data.errors.base || ['Erro ao buscar dados do cartão.'])
      },
    })
  }

  const handleRedirectToConsumptionLogs = ({ eventId }: { eventId: string }) => {
    const param = createSearchParams({
      'model[label]': 'Consumo (Empresa Cliente)',
      'model[value]': 'Client::Consumption',
      'itemId': eventId
    })

    navigate({
      pathname: '/logs',
      search: param.toString()
    })
  }

  const {
    data: companyBillingEvents,
    meta,
    isPending: isPendingCompanyBillingEvents,
    isFetching: isFetchingCompanyBillingEvents,
    error: companyBillingEventsError = {} as any,
    refetch: refetchCompanyBillingEvents,
  } = useBillingEvents({
    filters: {
      search: search,
      page: filterParams.page || 1,
      clientCompanyId: companyId || '',
      createdAtStartDate: formattedDate.from,
      createdAtEndDate: formattedDate.to,
      clientBranchId: filterParams.branch?.value,
      equipmentId: filterParams.equipment?.value,
      establishmentBranchId: filterParams.establishmentCompany?.value,
      fromInternalBase: filterParams.fromInternalBase?.value,
      authorId: filterParams.user?.value,
      productId: filterParams.product?.value,
    },
    mapper: Mapper.mapWithTransformer<BillingEventData[], CompanyBillingEventTableItem[]>({
      transformer: CompanyBillingEventTransformer.tableTransformer({
        redirectToConsumption: handleRedirectToConsumption,
        redirectToConsumptionLogs: handleRedirectToConsumptionLogs,
        disableActions: cardDataIsPending,
      }),
    }),
  })

  useEffect(() => {
    if (filterParams.search !== instantSearch)
      setInstantSearch(filterParams.search);
  }, [filterParams.search]);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyBillingEventsError?.status}
      refetchFunction={refetchCompanyBillingEvents}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <TextField
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value)
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
            }}
            value={filterParams.search}
          />

          <div className="flex items-center justify-between">
            <Filters companyId={companyId} />

            <div className='flex gap-2 items-center'>
              <LoadingSpinner isLoading={isFetchingCompanyBillingEvents} />
              {companyKind === 'private_company' && (
                <div className='flex items-center gap-4'>
                  <div>
                    <p className='text-sm italic font-thin text-white-400'>Limite disponível</p>
                    <span className='text-lg'>{formatCurrency(Number(remainingCredit))}</span>
                  </div>
                  <div>
                    <p className='text-sm italic font-thin text-white-400'>Saldo disponível</p>
                    <span className='text-valid text-lg'>{formatCurrency(Number(remainingBalance))}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <Table
            className="max-h-[calc(100vh-356px)]"
            columns={companyKind === 'private_company' ? defaultColumns : defaultColumns.filter((column) => column.value !== 'from_client_balance')}
            isLoadingData={isPendingCompanyBillingEvents}
            page={filterParams.page || 1}
            rows={companyBillingEvents}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            rowEmptyValue="--"
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </div>

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </ErrorMessageWrapper>
  )
}

export default List
